import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getAvailabilityForDateRange, 
  getAllServices, 
  adjustAvailabilityForServices,
  checkTimeSlotAvailability,
  getAvailableTimeSlots
} from '../firebase/appointments';
import { formatDateBR, convertDateBRToISO } from '../utils/dateUtils';
import { parseISO, format } from 'date-fns';

/**
 * Serviço de cache para disponibilidade
 */
const availabilityCache = {
  /**
   * Obtém dados do cache
   * @param {string} key - Chave do cache
   * @returns {object|null} - Dados do cache ou null se não existir/expirado
   */
  get(key) {
    try {
      const rawData = localStorage.getItem(key);
      if (!rawData) return null;
      
      const cacheItem = JSON.parse(rawData);
      
      // Verificar expiração
      if (cacheItem.timestamp && Date.now() - cacheItem.timestamp > cacheItem.ttl) {
        this.remove(key);
        return null;
      }
      
      return cacheItem.data;
    } catch (error) {
      console.error('Erro ao ler cache de disponibilidade:', error);
      return null;
    }
  },

  /**
   * Salva dados no cache
   * @param {string} key - Chave do cache
   * @param {object} data - Dados a serem armazenados
   * @param {number} ttl - Tempo de vida em ms (padrão: 30min)
   */
  set(key, data, ttl = 1800000) {
    try {
      const cacheItem = {
        timestamp: Date.now(),
        ttl,
        data
      };
      
      localStorage.setItem(key, JSON.stringify(cacheItem));
    } catch (error) {
      console.error('Erro ao salvar cache de disponibilidade:', error);
      // Em navegação anônima ou quando localStorage falha, criar cache em memória
      this._memoryCache = this._memoryCache || {};
      this._memoryCache[key] = {
        timestamp: Date.now(),
        ttl,
        data
      };
    }
  },

  /**
   * Remove um item específico do cache
   * @param {string} key - Chave a ser removida
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      // Também remover da memória se existir
      if (this._memoryCache && this._memoryCache[key]) {
        delete this._memoryCache[key];
      }
    } catch (error) {
      console.error('Erro ao remover item do cache:', error);
    }
  },

  /**
   * Limpa todos os caches de disponibilidade ou um padrão específico
   * @param {string} keyPattern - Padrão de chave a ser removido (opcional)
   */
  clear(keyPattern = null) {
    try {
      const prefix = 'availability_';
      
      if (keyPattern) {
        // Limpar apenas chaves que correspondem ao padrão
        const matchingKey = prefix + keyPattern;
        Object.keys(localStorage)
          .filter(key => key.startsWith(matchingKey))
          .forEach(key => localStorage.removeItem(key));
      } else {
        // Limpar todas as chaves de disponibilidade
        Object.keys(localStorage)
          .filter(key => key.startsWith(prefix))
          .forEach(key => localStorage.removeItem(key));
      }
      
      // Limpar cache em memória também
      if (this._memoryCache) {
        if (keyPattern) {
          const matchingKey = prefix + keyPattern;
          Object.keys(this._memoryCache)
            .filter(key => key.startsWith(matchingKey))
            .forEach(key => delete this._memoryCache[key]);
        } else {
          Object.keys(this._memoryCache)
            .filter(key => key.startsWith(prefix))
            .forEach(key => delete this._memoryCache[key]);
        }
      }
    } catch (error) {
      console.error('Erro ao limpar cache de disponibilidade:', error);
    }
  },
  
  /**
   * Cache de memória para quando localStorage falha (navegação anônima)
   */
  _memoryCache: {},
  
  /**
   * Verificar se o localStorage está disponível
   * @returns {boolean} - true se localStorage estiver disponível
   */
  isStorageAvailable() {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn('localStorage não disponível. Usando cache em memória.');
      return false;
    }
  },

  /**
   * Obtém dados do cache para slots combinados de profissionais
   * @param {string} salonId - ID do salão
   * @param {string} date - Data no formato ISO (YYYY-MM-DD)
   * @param {Array} serviceIds - IDs dos serviços selecionados
   * @returns {object|null} - Dados do cache ou null se não existir/expirado
   */
  getCombinedSlots(salonId, date, serviceIds) {
    const serviceKey = Array.isArray(serviceIds) ? serviceIds.sort().join('_') : 'all';
    const key = `availability_combined_${salonId}_${date}_${serviceKey}`;
    
    // Verificar primeiro no localStorage
    const localData = this.get(key);
    if (localData) return localData;
    
    // Se localStorage falhou, verificar no memoryCache
    if (this._memoryCache && this._memoryCache[key]) {
      const cacheItem = this._memoryCache[key];
      
      // Verificar expiração
      if (cacheItem.timestamp && Date.now() - cacheItem.timestamp > cacheItem.ttl) {
        delete this._memoryCache[key];
        return null;
      }
      
      return cacheItem.data;
    }
    
    return null;
  },

  /**
   * Salva dados combinados no cache
   * @param {string} salonId - ID do salão
   * @param {string} date - Data no formato ISO (YYYY-MM-DD)
   * @param {Array} serviceIds - IDs dos serviços selecionados
   * @param {object} data - Dados a serem armazenados
   * @param {number} ttl - Tempo de vida em ms (padrão: 10min)
   */
  setCombinedSlots(salonId, date, serviceIds, data, ttl = 600000) {
    const serviceKey = Array.isArray(serviceIds) ? serviceIds.sort().join('_') : 'all';
    const key = `availability_combined_${salonId}_${date}_${serviceKey}`;
    this.set(key, data, ttl);
  }
};

/**
 * Hook para gerenciar dados de disponibilidade de horários
 * @param {string} salonId - ID do salão
 * @param {object} options - Opções de configuração
 * @returns {Object} - Estado e funções para gerenciar disponibilidade
 */
export const useAvailability = (salonId, options = {}) => {
  // Estado para armazenar mapa de disponibilidade
  const [availabilityMap, setAvailabilityMap] = useState({});
  
  // Estado para informações de carregamento
  const [loadingState, setLoadingState] = useState({});
  
  // Estado para controlar carregamento global
  const [loading, setLoading] = useState(false);
  
  // Estado para armazenar serviços
  const [services, setServices] = useState([]);
  
  // Estado de erro
  const [error, setError] = useState(null);
  
  // Estado para slots disponíveis
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Mapa para controlar requisições em andamento e evitar duplicações
  const pendingRequests = useRef({});
  
  // Flag para controlar inicialização
  const initialized = useRef(false);
  
  // Adicionando novo estado para profissionais disponíveis
  const [availableProfessionalsMap, setAvailableProfessionalsMap] = useState({});
  
  // Obter opções com valores padrão
  const { 
    useCaching = true, 
    cacheTTL = 1800000, // 30 minutos
    preloadDays = 7
  } = options;
  
  // Verificar se localStorage está disponível
  const isStorageAvailable = useRef(availabilityCache.isStorageAvailable());
  
  // Buscar serviços ao inicializar
  useEffect(() => {
    const fetchServices = async () => {
      if (!salonId) return;
      
      try {
        const servicesData = await getAllServices(salonId);
        setServices(servicesData);
      } catch (error) {
        console.error('Erro ao buscar serviços:', error);
        setError('Erro ao carregar serviços');
      }
    };
    
    fetchServices();
  }, [salonId]);
  
  // Função para gerar chave de cache para uma requisição
  const getCacheKey = useCallback((startDate, endDate, professionalId = null) => {
    const baseKey = professionalId 
      ? `${startDate}|${endDate}|${professionalId}` 
      : `${startDate}|${endDate}`;
      
    return `availability_${salonId}_${baseKey}`;
  }, [salonId]);
  
  // Função para obter disponibilidade do servidor ou cache
  const fetchAvailability = useCallback(async (startDate, endDate, professionalId = null, forceRefresh = false) => {
    if (!salonId) return null;
    
    const cacheKey = getCacheKey(startDate, endDate, professionalId);
    
    // Verificar cache se permitido e não está forçando atualização
    if (useCaching && !forceRefresh) {
      const cachedData = availabilityCache.get(cacheKey);
      if (cachedData) {
        console.log(`[useAvailability] Usando dados em cache para ${cacheKey}`);
        return cachedData;
      }
    }
    
    try {
      // Buscar dados do servidor
      const data = await getAvailabilityForDateRange(
        salonId, 
        startDate, 
        endDate, 
        professionalId
      );
      
      // Salvar no cache se permitido
      if (useCaching) {
        availabilityCache.set(cacheKey, data, cacheTTL);
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar disponibilidade:', error);
      setError('Erro ao carregar disponibilidade');
      return null;
    }
  }, [salonId, getCacheKey, useCaching, cacheTTL]);
  
  // Função para pré-carregar disponibilidade
  const preloadAvailability = useCallback(async (startDate, endDate, professionalId = null, forceRefresh = false) => {
    if (!salonId) return;
    
    // Gerar chave de cache baseada nos parâmetros
    const cacheKey = getCacheKey(startDate, endDate, professionalId);
    
    // Se já está carregado e não é forçado o refresh, retornar diretamente
    if (!forceRefresh && loadingState[cacheKey] === 'loaded') {
      console.log(`[useAvailability] Dados já carregados para ${cacheKey}`);
      return availabilityMap;
    }
    
    // Verificar se já existe uma requisição pendente para esta chave
    if (pendingRequests.current[cacheKey]) {
      console.log(`[useAvailability] Requisição pendente para ${cacheKey}, aguardando...`);
      
      try {
        // Aguardar a requisição existente completar
        return await pendingRequests.current[cacheKey];
      } catch (error) {
        console.error(`[useAvailability] Erro ao aguardar requisição pendente ${cacheKey}:`, error);
        delete pendingRequests.current[cacheKey];
        return null;
      }
    }
    
    // Atualizar estado de carregamento
    setLoadingState(prev => ({ ...prev, [cacheKey]: 'loading' }));
    
    // Criar promessa para esta requisição
    const requestPromise = (async () => {
      try {
        // Verificar cache primeiro
        const cachedData = useCaching && !forceRefresh ? availabilityCache.get(cacheKey) : null;
        if (cachedData) {
          console.log(`[useAvailability] Usando dados em cache para ${cacheKey}`);
          
          // Atualizar mapa de disponibilidade (mesclar com dados existentes)
          setAvailabilityMap(prev => {
            // Criar cópia do mapa atual
            const newMap = { ...prev };
            
            // Para cada data no resultado
            Object.keys(cachedData).forEach(date => {
              // Se a data já existe, mesclar profissionais
              if (newMap[date]) {
                newMap[date] = { ...newMap[date], ...cachedData[date] };
              } else {
                // Caso contrário, adicionar a data
                newMap[date] = cachedData[date];
              }
            });
            
            return newMap;
          });
          
          // Atualizar estado de carregamento
          setLoadingState(prev => ({ ...prev, [cacheKey]: 'loaded' }));
          
          return cachedData;
        }
        
        // Buscar dados do servidor
        const data = await getAvailabilityForDateRange(
          salonId, 
          startDate, 
          endDate, 
          professionalId
        );
        
        // Salvar no cache se permitido
        if (useCaching) {
          availabilityCache.set(cacheKey, data, cacheTTL);
        }
        
        // Atualizar mapa de disponibilidade (mesclar com dados existentes)
        setAvailabilityMap(prev => {
          // Criar cópia do mapa atual
          const newMap = { ...prev };
          
          // Para cada data no resultado
          Object.keys(data).forEach(date => {
            // Se a data já existe, mesclar profissionais
            if (newMap[date]) {
              newMap[date] = { ...newMap[date], ...data[date] };
            } else {
              // Caso contrário, adicionar a data
              newMap[date] = data[date];
            }
          });
          
          return newMap;
        });
        
        // Atualizar estado de carregamento
        setLoadingState(prev => ({ ...prev, [cacheKey]: 'loaded' }));
        
        return data;
      } catch (error) {
        console.error('Erro ao pré-carregar disponibilidade:', error);
        setLoadingState(prev => ({ ...prev, [cacheKey]: 'error' }));
        setError('Erro ao carregar disponibilidade');
        throw error;
      } finally {
        // Limpar requisição pendente
        delete pendingRequests.current[cacheKey];
      }
    })();
    
    // Registrar a promessa nas requisições pendentes
    pendingRequests.current[cacheKey] = requestPromise;
    
    // Adicionar timeout automático para cancelar requisição após 30 segundos
    const timeoutId = setTimeout(() => {
      if (pendingRequests.current[cacheKey]) {
        console.log(`[useAvailability] Timeout para requisição ${cacheKey}, limpando...`);
        delete pendingRequests.current[cacheKey];
        setLoadingState(prev => ({ ...prev, [cacheKey]: 'error' }));
      }
    }, 30000); // 30 segundos
    
    // Assegurar que o timeout seja limpo quando a Promise resolver ou rejeitar
    requestPromise.finally(() => clearTimeout(timeoutId));
    
    try {
      return await requestPromise;
    } catch (error) {
      console.error(`[useAvailability] Erro ao carregar disponibilidade para ${cacheKey}:`, error);
      return null;
    }
  }, [salonId, availabilityMap, loadingState, getCacheKey, useCaching, cacheTTL]);
  
  // Função para pré-carregar um intervalo de datas
  const preloadDateRange = useCallback(async (startDate, endDate, professionalId = null) => {
    return preloadAvailability(startDate, endDate, professionalId);
  }, [preloadAvailability]);
  
  // Função para forçar atualização de disponibilidade
  const refreshAvailability = useCallback(async (startDate, endDate, professionalId = null) => {
    return preloadAvailability(startDate, endDate, professionalId, true);
  }, [preloadAvailability]);
  
  // Função para limpar cache de disponibilidade
  const clearCache = useCallback((keyPattern = null) => {
    console.log(`[useAvailability] Limpando cache ${keyPattern ? 'para ' + keyPattern : 'completo'}`);
    
    // Limpar o cache externo
    availabilityCache.clear(keyPattern);
    
    // Limpar requisições pendentes relacionadas
    if (keyPattern) {
      Object.keys(pendingRequests.current)
        .filter(key => key.includes(keyPattern))
        .forEach(key => {
          delete pendingRequests.current[key];
          console.log(`[useAvailability] Requisição pendente ${key} cancelada`);
        });
    } else {
      // Limpar todas as requisições pendentes
      pendingRequests.current = {};
    }
    
    // Atualizar estado de loading para as chaves afetadas
    setLoadingState(prev => {
      const newState = { ...prev };
      
      if (keyPattern) {
        Object.keys(newState)
          .filter(key => key.includes(keyPattern))
          .forEach(key => {
            delete newState[key];
          });
      } else {
        // Limpar todo o estado de loading
        return {};
      }
      
      return newState;
    });
  }, []);
  
  // Função para invalidar cache por data
  const invalidateDateCache = useCallback((date) => {
    if (!date) return;
    
    console.log(`[useAvailability] Invalidando cache para data ${date}`);
    
    // Limpar tudo relacionado a esta data
    clearCache(`_${date}`);
    
    // Remover do mapa de disponibilidade
    setAvailabilityMap(prev => {
      const newMap = { ...prev };
      delete newMap[date];
      return newMap;
    });
  }, [clearCache]);
  
  // Função para invalidar cache por profissional
  const invalidateProfessionalCache = useCallback((professionalId) => {
    if (!professionalId) return;
    
    console.log(`[useAvailability] Invalidando cache para profissional ${professionalId}`);
    
    // Limpar tudo relacionado a este profissional
    clearCache(professionalId);
    
    // Remover do mapa de disponibilidade
    setAvailabilityMap(prev => {
      const newMap = { ...prev };
      
      // Para cada data, remover o profissional
      Object.keys(newMap).forEach(date => {
        if (newMap[date] && newMap[date][professionalId]) {
          const newDateMap = { ...newMap[date] };
          delete newDateMap[professionalId];
          newMap[date] = newDateMap;
        }
      });
      
      return newMap;
    });
  }, [clearCache]);
  
  // Adicionar função para forçar atualização de todos os dados
  const forceRefreshAll = useCallback(async () => {
    console.log('[useAvailability] Forçando atualização completa de dados');
    
    // Limpar cache em memória e localStorage
    availabilityCache.clear();
    
    // Limpar o mapa de disponibilidade
    setAvailabilityMap({});
    
    // Limpar loading state e requisições pendentes
    setLoadingState({});
    pendingRequests.current = {};
    
    // Reiniciar inicialização
    initialized.current = false;
    
    // Recarregar dados
    const today = new Date();
    const lastDay = new Date(today);
    lastDay.setDate(today.getDate() + preloadDays);
    
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    return preloadAvailability(formatDate(today), formatDate(lastDay), null, true);
  }, [preloadAvailability, preloadDays]);
  
  // Pré-carregar próximos dias ao inicializar (apenas uma vez)
  useEffect(() => {
    if (!salonId || initialized.current) return;
    
    const initializeAvailabilityData = async () => {
      initialized.current = true;
      
      try {
        // Limpar qualquer requisição pendente no início
        pendingRequests.current = {};
        
        const today = new Date();
        const lastDay = new Date(today);
        lastDay.setDate(today.getDate() + preloadDays);
        
        const formatDate = (date) => {
          return date.toISOString().split('T')[0];
        };
        
        console.log(`[useAvailability] Inicializando dados para período ${formatDate(today)} a ${formatDate(lastDay)}`);
        
        // Carregar dados gerais (sem profissional específico)
        await preloadAvailability(formatDate(today), formatDate(lastDay));
      } catch (error) {
        console.error('Erro na inicialização de disponibilidade:', error);
        setError('Erro ao inicializar disponibilidade');
      } finally {
        // Marcar que tentamos inicializar, mesmo se falhou
        initialized.current = true;
      }
    };
    
    initializeAvailabilityData();
  }, [salonId, preloadAvailability, preloadDays]);
  
  // Função para obter slots para uma data, profissional e serviços específicos
  const getTimeSlotsForServices = useCallback((date, professionalId, serviceIds) => {
    // Se não temos dados para esta data/profissional, retornar array vazio
    if (!availabilityMap[date] || !availabilityMap[date][professionalId]) {
      // Tentar carregar os dados, mas retornar array vazio por enquanto
      if (salonId && date && professionalId) {
        preloadAvailability(date, date, professionalId);
      }
      return [];
    }
    
    // Ajustar slots para os serviços selecionados
    return adjustAvailabilityForServices(
      availabilityMap,
      date,
      professionalId,
      serviceIds,
      services
    );
  }, [availabilityMap, services, salonId, preloadAvailability]);
  
  // Função para verificar disponibilidade de um slot específico
  const checkSlotAvailability = useCallback(async (
    date,
    time,
    professionalId,
    serviceIds,
    excludeAppointmentId = null
  ) => {
    if (!salonId || !date || !time || !professionalId || !serviceIds) {
      return {
        available: false,
        conflicts: [],
        error: 'Parâmetros inválidos'
      };
    }
    
    try {
      // Verificar disponibilidade diretamente (sem cache)
      const availability = await checkTimeSlotAvailability(
        salonId,
        date,
        time,
        professionalId,
        serviceIds,
        excludeAppointmentId
      );
      
      return availability;
    } catch (error) {
      console.error('Erro ao verificar disponibilidade de slot:', error);
      setError('Erro ao verificar disponibilidade');
      return {
        available: false,
        conflicts: [],
        error: error.message || 'Erro desconhecido'
      };
    }
  }, [salonId]);
  
  // Função para obter as datas disponíveis dentro de um intervalo
  const getAvailableDates = useCallback(async (startDate, endDate, professionalId = null) => {
    console.log(`[useAvailability] getAvailableDates chamado para ${startDate} a ${endDate}`);
    
    // Verificar se temos salonId
    if (!salonId) {
      console.error('[useAvailability] getAvailableDates: salonId não fornecido');
      return [];
    }
    
    // Criar uma chave de identificação única para esta solicitação
    const requestKey = `availabledates_${startDate}_${endDate}_${professionalId || 'all'}`;
    
    // Verificar se já existe uma requisição pendente para esta combinação
    if (pendingRequests.current[requestKey]) {
      console.log(`[useAvailability] Requisição pendente para datas disponíveis, aguardando...`);
      try {
        return await pendingRequests.current[requestKey];
      } catch (error) {
        delete pendingRequests.current[requestKey];
        return [];
      }
    }
    
    // Criar uma promessa para esta requisição
    const requestPromise = (async () => {
      try {
        // Verificar cache primeiro
        const cacheKey = `availabledates_${salonId}_${startDate}_${endDate}_${professionalId || 'all'}`;
        if (useCaching) {
          const cachedDates = availabilityCache.get(cacheKey);
          if (cachedDates) {
            console.log(`[useAvailability] Usando cache para datas disponíveis: ${startDate} a ${endDate}`);
            return cachedDates;
          }
        }
        
        // Certificar que os dados estão carregados
        console.log(`[useAvailability] Carregando dados para periodo ${startDate} a ${endDate}`);
        const availabilityData = await preloadAvailability(startDate, endDate, professionalId);
        
        if (!availabilityData || Object.keys(availabilityData).length === 0) {
          console.warn(`[useAvailability] Nenhum dado disponível para ${startDate} a ${endDate}`);
          
          // Tentar criar uma lista básica de datas para o intervalo
          const fallbackDates = [];
          const currentDate = new Date(startDate);
          const endDateObj = new Date(endDate);
          
          while (currentDate <= endDateObj) {
            const dateStr = currentDate.toISOString().split('T')[0];
            
            fallbackDates.push({
              date: dateStr,
              formattedDate: formatDateBR(dateStr),
              available: true
            });
            
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          if (fallbackDates.length > 0) {
            console.log(`[useAvailability] Usando datas fallback (${fallbackDates.length} geradas)`);
            return fallbackDates;
          }
          
          return [];
        }
        
        // Filtrar apenas datas que têm pelo menos um slot disponível
        const availableDates = Object.keys(availabilityMap)
          .filter(date => {
            // Verificar se a data está no intervalo
            if (date < startDate || date > endDate) return false;
            
            // Se temos um profissional específico, verificar apenas para ele
            if (professionalId) {
              const professionalSlots = availabilityMap[date]?.[professionalId] || [];
              return professionalSlots.some(slot => slot.available);
            }
            
            // Caso contrário, verificar se algum profissional tem slot disponível
            const professionals = availabilityMap[date] || {};
            return Object.values(professionals).some(
              slots => slots && Array.isArray(slots) && slots.some(slot => slot.available)
            );
          })
          .map(date => ({
            date,
            formattedDate: formatDateBR(date),
            available: true
          }));
        
        // Se não há datas filtradas mas temos dados, mostrar todas as datas
        if (availableDates.length === 0 && Object.keys(availabilityData).length > 0) {
          console.log(`[useAvailability] Usando todas as datas por nenhuma ser considerada disponível`);
          const allDates = Object.keys(availabilityData)
            .filter(date => date >= startDate && date <= endDate)
            .map(date => ({
              date,
              formattedDate: formatDateBR(date),
              available: true
            }));
            
          // Salvar no cache
          if (useCaching && allDates.length > 0) {
            availabilityCache.set(cacheKey, allDates, 600000); // 10 minutos
          }
          
          return allDates;
        }
        
        console.log(`[useAvailability] Datas disponíveis: ${availableDates.length}`);
        
        // Salvar no cache
        if (useCaching) {
          availabilityCache.set(cacheKey, availableDates, 600000); // 10 minutos
        }
        
        return availableDates;
      } catch (error) {
        console.error('Erro ao obter datas disponíveis:', error);
        return [];
      } finally {
        delete pendingRequests.current[requestKey];
      }
    })();
    
    // Registrar a promessa
    pendingRequests.current[requestKey] = requestPromise;
    
    // Adicionar timeout para limpar requisição após 15 segundos
    setTimeout(() => {
      if (pendingRequests.current[requestKey]) {
        console.log(`[useAvailability] Timeout para obtenção de datas, limpando...`);
        delete pendingRequests.current[requestKey];
      }
    }, 15000);
    
    return requestPromise;
  }, [availabilityMap, preloadAvailability, salonId, useCaching]);
  
  // Nova função para filtrar horários passados - MOVIDA PARA ANTES de getAvailableTimeSlots
  const filterPastTimeSlots = useCallback((slots, date) => {
    const today = new Date();
    const selectedDateObj = parseISO(date);
    
    const isToday = 
      today.getDate() === selectedDateObj.getDate() &&
      today.getMonth() === selectedDateObj.getMonth() &&
      today.getFullYear() === selectedDateObj.getFullYear();
    
    if (!isToday) return slots;
    
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    
    return slots.filter(slot => {
      const [slotHour, slotMinute] = slot.time.split(':').map(Number);
      
      if (slotHour > currentHour) return true;
      if (slotHour === currentHour) return slotMinute > currentMinute;
      
      return false;
    });
  }, []);
  
  // Função para obter horários disponíveis para uma data
  const getAvailableTimeSlots = useCallback(async (date, professionalId, serviceIds = []) => {
    console.log(`[useAvailability] Carregando horários para ${date} e profissional ${professionalId}`);
    
    if (!salonId || !date || !professionalId) {
      console.log('[useAvailability] Parâmetros insuficientes para getAvailableTimeSlots');
      return [];
    }
    
    // Criar uma chave de identificação única para esta solicitação
    const requestKey = `timeslots_${date}_${professionalId}_${serviceIds.sort().join('_')}`;
    
    // Verificar se já existe uma requisição pendente para esta combinação
    if (pendingRequests.current[requestKey]) {
      console.log(`[useAvailability] Requisição pendente para ${requestKey}, aguardando...`);
      try {
        return await pendingRequests.current[requestKey];
      } catch (error) {
        delete pendingRequests.current[requestKey];
        return [];
      }
    }
    
    // Criar uma promessa para esta requisição
    const requestPromise = (async () => {
      try {
        setLoading(true);
        
        // Verificar cache primeiro
        const cacheKey = `availableslots_${salonId}_${date}_${professionalId}_${serviceIds.sort().join('_')}`;
        if (useCaching) {
          const cachedSlots = availabilityCache.get(cacheKey);
          if (cachedSlots) {
            console.log(`[useAvailability] Usando cache para slots: ${date}/${professionalId}`);
            return cachedSlots;
          }
        }
        
        // Certificar que os dados estão carregados
        await preloadAvailability(date, date, professionalId);
        
        // Obter slots ajustados para os serviços
        const slots = getTimeSlotsForServices(date, professionalId, serviceIds);
        console.log(`[useAvailability] Encontrados ${slots.length} slots antes de filtrar`);
        
        // Filtrar horários passados se for necessário
        const filteredSlots = filterPastTimeSlots(slots, date);
        console.log(`[useAvailability] Após filtrar datas passadas: ${filteredSlots.length} slots`);
        
        // Salvar no cache
        if (useCaching) {
          availabilityCache.set(cacheKey, filteredSlots, 600000); // 10 minutos
        }
        
        return filteredSlots;
      } catch (error) {
        console.error('[useAvailability] Erro ao carregar horários disponíveis:', error);
        return [];
      } finally {
        setLoading(false);
        delete pendingRequests.current[requestKey];
      }
    })();
    
    // Registrar a promessa
    pendingRequests.current[requestKey] = requestPromise;
    
    // Adicionar timeout para limpar requisição após 15 segundos
    setTimeout(() => {
      if (pendingRequests.current[requestKey]) {
        console.log(`[useAvailability] Timeout para requisição ${requestKey}, limpando...`);
        delete pendingRequests.current[requestKey];
      }
    }, 15000);
    
    return requestPromise;
  }, [salonId, preloadAvailability, getTimeSlotsForServices, filterPastTimeSlots, useCaching]);
  
  // Função para obter horários disponíveis combinados de todos os profissionais
  const getCombinedAvailableTimeSlots = useCallback(async (date, professionalsList, serviceIds = []) => {
    console.log(`[useAvailability] Carregando horários combinados para ${date} com ${professionalsList?.length || 0} profissionais`);
    
    if (!salonId || !date || !professionalsList || professionalsList.length === 0) {
      console.log('[useAvailability] Parâmetros insuficientes para getCombinedAvailableTimeSlots');
      return [];
    }
    
    // Evitar chamar esta função repetidamente para os mesmos parâmetros
    // Criar uma chave única baseada nos parâmetros
    const requestKey = `combined_${salonId}_${date}_${serviceIds.sort().join('_')}`;
    
    // Verificar se já existe uma requisição pendente para esta combinação
    if (pendingRequests.current[requestKey]) {
      console.log(`[useAvailability] Já existe requisição pendente para slots combinados: ${date}`);
      return pendingRequests.current[requestKey];
    }
    
    setLoading(true);
    try {
      // Verificação robusta do parâmetro professionals
      if (!professionalsList) {
        console.error('[useAvailability] getCombinedAvailableTimeSlots: parâmetro professionals é undefined');
        return [];
      }
      
      if (!Array.isArray(professionalsList)) {
        console.error('[useAvailability] getCombinedAvailableTimeSlots: parâmetro professionals não é um array', typeof professionalsList);
        return [];
      }
      
      if (professionalsList.length === 0) {
        console.warn('[useAvailability] getCombinedAvailableTimeSlots: array de professionals está vazio');
        return [];
      }
      
      console.log(`[useAvailability] Obtendo slots combinados para ${date} com ${professionalsList.length} profissionais:`, 
        professionalsList.map(p => ({ id: p.id, nome: p.nome || p.name }))
      );
      
      // Verificar cache de slots combinados
      if (useCaching) {
        const cachedSlots = availabilityCache.getCombinedSlots(salonId, date, serviceIds);
        if (cachedSlots) {
          console.log(`[useAvailability] Usando cache para slots combinados: ${date}`);
          setLoading(false);
          return cachedSlots;
        }
      }
      
      // Criar uma promessa para esta requisição
      const requestPromise = (async () => {
        const availableSlotsMap = {};
        
        // Pré-carregar dados para todos os profissionais uma única vez
        await preloadAvailability(date, date, null);
        
        // Para cada profissional, verificar disponibilidade
        const professionalRequests = professionalsList.map(async (professional) => {
          try {
            if (!professional || !professional.id) {
              console.error('[useAvailability] Profissional inválido na lista:', professional);
              return;
            }
            
            // Obter slots para o profissional (usando dados já carregados em preloadAvailability)
            // Evitar chamadas independentes para cada profissional
            const profSlots = getTimeSlotsForServices(
              date,
              professional.id,
              serviceIds
            );
            
            // Adicionar slots disponíveis ao mapa
            profSlots.forEach(slot => {
              if (slot.available) {
                if (!availableSlotsMap[slot.time]) {
                  availableSlotsMap[slot.time] = { 
                    time: slot.time, 
                    available: true,
                    professionals: [professional.id]
                  };
                } else {
                  availableSlotsMap[slot.time].professionals.push(professional.id);
                }
              }
            });
          } catch (profError) {
            console.error(`Erro ao carregar slots para profissional ${professional?.id}:`, profError);
          }
        });
        
        await Promise.all(professionalRequests);
        
        // Converter mapa para array
        const combinedSlots = Object.values(availableSlotsMap);
        combinedSlots.sort((a, b) => a.time.localeCompare(b.time));
        
        // Salvar no cache
        if (useCaching) {
          availabilityCache.setCombinedSlots(salonId, date, serviceIds, combinedSlots);
        }
        
        // Limpar a requisição pendente
        delete pendingRequests.current[requestKey];
        
        return combinedSlots;
      })();
      
      // Registrar a promessa
      pendingRequests.current[requestKey] = requestPromise;
      
      // Adicionar timeout para limpar requisição após 15 segundos
      setTimeout(() => {
        if (pendingRequests.current[requestKey]) {
          console.log(`[useAvailability] Timeout para requisição combinada ${date}, limpando...`);
          delete pendingRequests.current[requestKey];
        }
      }, 15000);
      
      const result = await requestPromise;
      return result;
    } catch (error) {
      console.error('[useAvailability] Erro ao carregar horários combinados:', error);
      delete pendingRequests.current[requestKey];
      return [];
    } finally {
      setLoading(false);
    }
  }, [salonId, preloadAvailability, getTimeSlotsForServices, useCaching]);
  
  // Função expandida para obter profissionais disponíveis com informações completas
  const getAvailableProfessionals = useCallback(async (date, time, serviceIds, allProfessionals = []) => {
    if (!salonId || !date || !time || !serviceIds || !Array.isArray(serviceIds) || serviceIds.length === 0) {
      return [];
    }
    
    try {
      // Verificar cache
      const cacheKey = `available_professionals_${date}_${time}_${serviceIds.sort().join('_')}`;
      if (useCaching) {
        const cachedProfessionals = availabilityCache.get(cacheKey);
        if (cachedProfessionals) {
          console.log(`[useAvailability] Usando cache para profissionais: ${date}_${time}`);
          return cachedProfessionals;
        }
      }
      
      // Pré-carrega a data para todos os profissionais
      await preloadAvailability(date, date, null);
      
      // Filtra os profissionais que estão disponíveis nesta data/horário
      const professionalIdsAvailable = [];
      
      // Para cada profissional mapeado na data
      for (const professionalId in availabilityMap[date] || {}) {
        const slots = availabilityMap[date][professionalId];
        
        // Encontrar o slot específico
        const slot = slots.find(s => s.time === time);
        
        // Se o slot existe e está disponível, adicionar o profissional
        if (slot && slot.available) {
          // Verificar disponibilidade considerando os serviços selecionados
          const professionalSlots = getTimeSlotsForServices(date, professionalId, serviceIds);
          const availableSlot = professionalSlots.find(s => s.time === time && s.available);
          
          if (availableSlot) {
            // Adiciona o ID do profissional na lista de disponíveis
            professionalIdsAvailable.push(professionalId);
          }
        }
      }
      
      // Se temos informações completas dos profissionais, enriquecer os resultados
      let availableProfs = professionalIdsAvailable.map(id => ({ id }));
      
      if (allProfessionals && allProfessionals.length > 0) {
        availableProfs = professionalIdsAvailable.map(profId => {
          const professionalInfo = allProfessionals.find(p => p.id === profId);
          return professionalInfo || { id: profId, nome: 'Profissional' };
        });
      }
      
      // Salvar no cache
      if (useCaching) {
        availabilityCache.set(cacheKey, availableProfs, 600000); // 10 minutos
      }
      
      return availableProfs;
    } catch (error) {
      console.error('Erro ao obter profissionais disponíveis:', error);
      setError('Erro ao obter profissionais disponíveis');
      return [];
    }
  }, [salonId, availabilityMap, preloadAvailability, getTimeSlotsForServices, useCaching]);
  
  // Verificar se uma data/profissional está carregada
  const isLoaded = useCallback((date, professionalId = null) => {
    return !!(
      availabilityMap[date] && 
      (!professionalId || availabilityMap[date][professionalId])
    );
  }, [availabilityMap]);
  
  // Verificar se uma data/profissional está carregando
  const isLoading = useCallback((startDate, endDate, professionalId = null) => {
    const cacheKey = getCacheKey(startDate, endDate, professionalId);
    return loadingState[cacheKey] === 'loading';
  }, [loadingState, getCacheKey]);
  
  // Nova função para carregar slots disponíveis para uma data específica
  const loadAvailableDatesInRange = useCallback(async (startDate, endDate) => {
    // Criar uma chave de identificação única para esta solicitação
    const requestKey = `loadavailabledates_${startDate}_${endDate}`;
    
    // Verificar se já existe uma requisição pendente para esta combinação
    if (pendingRequests.current[requestKey]) {
      console.log(`[useAvailability] Requisição pendente para carregar datas, aguardando...`);
      try {
        return await pendingRequests.current[requestKey];
      } catch (error) {
        delete pendingRequests.current[requestKey];
        return [];
      }
    }
    
    // Criar uma promessa para esta requisição
    const requestPromise = (async () => {
      try {
        // Verificar cache primeiro
        const cacheKey = `loaddates_${salonId}_${startDate}_${endDate}`;
        if (useCaching) {
          const cachedSlots = availabilityCache.get(cacheKey);
          if (cachedSlots) {
            console.log(`[useAvailability] Usando cache para carregar datas: ${startDate} a ${endDate}`);
            return cachedSlots;
          }
        }
        
        // Obter datas disponíveis no intervalo
        const availableDates = await getAvailableDates(startDate, endDate);
        
        // Mapear para formato compatível com o calendário
        const mappedSlots = availableDates.map(dateInfo => ({
          date: dateInfo.date,
          time: '00:00',
          available: dateInfo.available
        }));
        
        // Salvar no cache
        if (useCaching) {
          availabilityCache.set(cacheKey, mappedSlots, 600000); // 10 minutos
        }
        
        return mappedSlots;
      } catch (error) {
        console.error('Erro ao obter datas disponíveis:', error);
        setError('Erro ao obter datas disponíveis');
        return [];
      } finally {
        delete pendingRequests.current[requestKey];
      }
    })();
    
    // Registrar a promessa
    pendingRequests.current[requestKey] = requestPromise;
    
    // Adicionar timeout para limpar requisição após 15 segundos
    setTimeout(() => {
      if (pendingRequests.current[requestKey]) {
        console.log(`[useAvailability] Timeout para carregar datas, limpando...`);
        delete pendingRequests.current[requestKey];
      }
    }, 15000);
    
    return requestPromise;
  }, [getAvailableDates, setError, salonId, useCaching]);

  // Dados e funções expostas pelo hook
  return {
    // Dados
    availabilityMap,
    availableSlots,
    services,
    loading: Object.values(loadingState).includes('loading'),
    error,
    isStorageAvailable: isStorageAvailable.current,
    
    // Métodos de carregamento/gestão
    preloadAvailability,
    preloadDateRange,
    refreshAvailability,
    clearCache,
    forceRefreshAll,
    invalidateDateCache,
    invalidateProfessionalCache,
    
    // Métodos de consulta
    getTimeSlotsForServices,
    getAvailableDates,
    getAvailableTimeSlots,
    getCombinedAvailableTimeSlots,
    getAvailableProfessionals,
    checkSlotAvailability,
    loadAvailableDatesInRange,
    
    // Métodos auxiliares
    isLoaded,
    isLoading,
    filterPastTimeSlots
  };
};

export default useAvailability; 